import { inject, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

import { NotificationSeverity, type NotificationDto } from 'api';
import { AuthService } from '@auth/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import { environment } from '@/environments/environment';

import { NotificationStoreService } from './notification-store.service';
import { normalizeSeverity } from './notification-meta';

const HUB_PATH = '/hubs/notifications';

/**
 * Conexión de tiempo real (SignalR) del sistema de notificaciones. Encapsula el `HubConnection`,
 * delega los eventos del servidor al `NotificationStoreService` y resincroniza tras reconectar.
 *
 * Tolerante a fallos: si el hub no conecta, la campanita sigue operando por REST.
 */
@Injectable({ providedIn: 'root' })
export class NotificationRealtimeService {
  private readonly auth = inject(AuthService);
  private readonly store = inject(NotificationStoreService);
  private readonly toast = inject(ToastService);

  private connection: HubConnection | null = null;

  /** Abre la conexión y registra los handlers. Idempotente: no reconecta si ya está activa. */
  async start(): Promise<void> {
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) return;

    const connection = this.createHubConnection();
    this.connection = connection;

    connection.on('ReceiveNotification', (notification: NotificationDto) => this.onReceiveNotification(notification));
    connection.on('UnreadCountChanged', (count: number) => this.store.setUnreadCount(count));
    connection.onreconnected(() => this.resync());

    try {
      await connection.start();
    } catch {
      // El hub no es crítico: la campanita funciona por REST aunque SignalR no conecte.
      this.connection = null;
    }
  }

  /** Cierra la conexión (logout). */
  async stop(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    if (connection) {
      try {
        await connection.stop();
      } catch {
        // Ignorar errores al cerrar.
      }
    }
  }

  /** Indica si la conexión está activa (uso interno/tests). */
  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  /**
   * Construye el `HubConnection`. Aislado en un método para poder sustituirlo en tests.
   * El token va por query string (`accessTokenFactory`) porque los WebSockets no envían headers;
   * se devuelve el token vigente del `AuthService` (que se auto-refresca antes de expirar).
   */
  protected createHubConnection(): HubConnection {
    return new HubConnectionBuilder()
      .withUrl(`${environment.backendBaseUrl}${HUB_PATH}`, {
        // El token va por query string (accessTokenFactory), no por cookies: deshabilitamos las
        // credenciales para que el handshake CORS funcione contra un backend con
        // `Access-Control-Allow-Origin: *` (el navegador prohíbe credenciales + origen comodín).
        accessTokenFactory: () => this.auth.token() ?? '',
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
  }

  private onReceiveNotification(notification: NotificationDto): void {
    this.store.pushRealtime(notification);
    // El badge no se incrementa aquí: desde 2026-07-02 el backend emite `UnreadCountChanged`
    // (valor absoluto) a todos los destinatarios también al CREAR la notificación.
    if (notification.title) {
      this.toast.show(this.toastSeverity(notification.severity), notification.title);
    }
  }

  /** Tras reconectar, vuelve a pedir el contador y la primera página para no perder eventos. */
  private resync(): void {
    this.store.refreshUnreadCount();
    this.store.loadFirstPage();
  }

  private toastSeverity(severity: unknown): 'info' | 'warn' | 'error' {
    switch (normalizeSeverity(severity)) {
      case NotificationSeverity.Critical:
        return 'error';
      case NotificationSeverity.Warning:
        return 'warn';
      default:
        return 'info';
    }
  }
}
